import {
  getTenantCodeFromPath,
  TenantPathResolutionError,
  withTenantInApiPath,
  isDevDashboardApiEndpoint,
} from 'pi-kiosk-shared';

let adminApiTenantPathOverride: string | null = null;

/** Pin API tenant to the active `/:tenant` route (set from Dashboard). */
export function setAdminApiTenantPathOverride(pathname: string | null): void {
  adminApiTenantPathOverride = pathname;
}

function resolvePathname(pathname?: string): string {
  if (pathname !== undefined) {
    return pathname;
  }
  if (adminApiTenantPathOverride) {
    return adminApiTenantPathOverride;
  }
  if (typeof window !== 'undefined') {
    return window.location.pathname;
  }
  return '/';
}

/** Tenant code from URL path (`/{tenant}/...`). Never uses homeTenantCode or env defaults. */
export function getTenantFromPath(pathname?: string): string | null {
  const path = resolvePathname(pathname);
  const fromPath = getTenantCodeFromPath(path);
  if (fromPath) {
    return fromPath;
  }
  if (path.startsWith('/dev')) {
    return null;
  }
  return null;
}

/**
 * Whether tenant-scoped admin catalog APIs (`/api/v1/admin/sales-points`, products,
 * bank-accounts, …) may be fetched.
 *
 * Prefer passing React Router `location.pathname` from hooks (Jest MemoryRouter does
 * not update `window.location`). Always refuses when the live window is on `/dev`,
 * so a stale `setAdminApiTenantPathOverride` cannot authorize bare catalog calls.
 */
export function canFetchTenantScopedAdminApis(pathname?: string): boolean {
  const windowPath =
    typeof window !== 'undefined' ? window.location.pathname : undefined;
  if (windowPath !== undefined && windowPath.startsWith('/dev')) {
    return false;
  }
  const path = pathname ?? windowPath ?? '/';
  if (path.startsWith('/dev')) {
    return false;
  }
  if (pathname !== undefined) {
    return getTenantCodeFromPath(pathname) != null;
  }
  return getTenantFromPath() != null;
}

export function requireTenantFromPath(pathname?: string): string {
  const tenant = getTenantFromPath(pathname);
  if (!tenant) {
    throw new TenantPathResolutionError(
      `Business partner required in URL path "${resolvePathname(pathname)}". Open /{tenantCode}/...`,
    );
  }
  return tenant;
}

/** UI keys (localStorage, links) — URL tenant only. */
export function getEffectiveTenantCodeForUi(pathname?: string): string {
  return requireTenantFromPath(pathname);
}

export { TenantPathResolutionError };

export function buildTenantApiBase(apiHost: string): string {
  try {
    const url = new URL(apiHost);
    return url.origin;
  } catch {
    return apiHost
      .replace(/\/+$/, '')
      .replace(/\/api\/?$/, '')
      .replace(/\/api\/?$/, '');
  }
}

export function buildTenantBaseName(): string {
  return '/';
}

/**
 * Insert tenant segment into API endpoint paths from the **URL** (`/{tenant}/...`).
 * Ignores authenticated homeTenantCode — same rule as kiosk and customer.
 */
export function withTenantInPath(
  endpoint: string,
  pathname?: string,
  tenantCodeOverride?: string,
): string {
  if (isDevDashboardApiEndpoint(endpoint)) {
    return endpoint;
  }

  const path = resolvePathname(pathname);
  const tenant = getTenantFromPath(path);
  const tenantCode = tenantCodeOverride ?? tenant;
  if (
    tenantCode === null &&
    (path.startsWith('/dev') ||
      (typeof window !== 'undefined' && window.location.pathname.startsWith('/dev')))
  ) {
    return endpoint;
  }

  if (!tenantCode) {
    throw new TenantPathResolutionError(
      `Cannot resolve business partner for API path "${endpoint}" — open /{tenantCode}/...`,
    );
  }

  return withTenantInApiPath(endpoint, tenantCode);
}

export function buildTenantKioskUrl(baseKioskUrl: string, pathname?: string): string {
  const tenant = getTenantFromPath(pathname);
  if (!tenant) {
    return baseKioskUrl;
  }

  const cleanBase = baseKioskUrl.replace(/\/+$/, '');
  return `${cleanBase}/${tenant}`;
}

export function buildKioskOpenUrl(
  tenantKioskBaseUrl: string,
  kioskId: number,
  kioskBootstrapSecret?: string | null,
): string {
  const cleanBase = tenantKioskBaseUrl.replace(/\/+$/, '');
  const url = new URL(`${cleanBase}/${kioskId}`);
  const secret = kioskBootstrapSecret?.trim();
  if (secret) {
    url.searchParams.set('secret', secret);
  }
  return url.toString();
}
